import React from "react";
import { usePageMeta } from "../lib/usePageMeta";
import { Link } from "react-router-dom";
import {
  Certificate, TestTube, Thermometer, ShieldCheck, Package, ArrowRight,
  ClipboardText,
} from "@phosphor-icons/react";

/**
 * Quality & Testing page — plain-English rewrite (2026-08).
 *
 * Rules honoured:
 *   * COA (Certificate of Analysis) is explained ONCE in plain English.
 *   * "Freeze-dried" is used as the plain equivalent of "lyophilized";
 *     the scientific term appears once, in parentheses.
 *   * NO invented purity %, cGMP / ISO / FDA certification, or
 *     manufacturing claim.
 *   * Every batch document exists per-lot when it has been reviewed,
 *     otherwise it is available on request — never a placeholder.
 *   * "Research use only" is stated explicitly.
 */
const PILLARS = [
  {
    icon: Certificate,
    title: "Batch documents when they’re ready",
    body: "Every published batch links to its own Certificate of Analysis (COA) — a lab document that identifies the material and its test results. If a batch’s COA hasn’t been reviewed and published yet, we say so. We don’t show generic or placeholder documents.",
    tid: "quality-pillar-batch-docs",
  },
  {
    icon: TestTube,
    title: "Only what we can back up",
    body: "We only publish identity, purity, or endotoxin numbers when they come from an independent test for that specific batch. If a number isn’t confirmed for a batch, it isn’t shown.",
    tid: "quality-pillar-only-backed",
  },
  {
    icon: Thermometer,
    title: "Room-temperature shipping",
    body: "Every research peptide ships as freeze-dried (lyophilized) powder in a tamper-evident box. Freeze-dried powder is stable at room temperature during transit — no cold-chain surcharges, no dry-ice complexity.",
    tid: "quality-pillar-room-temp",
  },
  {
    icon: Package,
    title: "Tracked, signature delivery",
    body: "Live carrier rates are quoted at checkout based on your address. Every parcel ships fully tracked and needs a signature at the receiving address on delivery.",
    tid: "quality-pillar-signature",
  },
  {
    icon: ShieldCheck,
    title: "Research use only",
    body: "Every product is sold strictly for in-vitro laboratory research. Not for human or animal use, and not intended to diagnose, treat, cure, or prevent any disease. This is stated in the cart and again at checkout.",
    tid: "quality-pillar-ruo",
  },
  {
    icon: ClipboardText,
    title: "Traceable inventory",
    body: "Inventory is picked batch-first and shipped with a packing list that names the batch. If a batch is put on hold or pulled, we know exactly where every affected unit has gone.",
    tid: "quality-pillar-traceable",
  },
];

export default function Quality() {
  usePageMeta({
    title: "Quality & Testing · How we handle documents",
    description:
      "How Medical Peptide Store handles batch documents (COAs), storage, room-temperature shipping, and signature delivery. Documents are published when reviewed, and otherwise available on request. For research use only.",
    canonicalPath: "/quality",
  });
  return (
    <div className="bg-white" data-testid="quality-page">
      {/* Intro band */}
      <section className="bg-brand-surface border-b border-brand-border">
        <div className="mps-container py-16 md:py-20 max-w-4xl">
          <div className="eyebrow mb-4">Quality &amp; Testing</div>
          <h1
            data-testid="quality-h1"
            className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-tight"
          >
            Documents that follow the batch — nothing invented.
          </h1>
          <p className="mt-6 text-brand-muted text-lg leading-relaxed max-w-2xl">
            We publish a Certificate of Analysis (COA) — a lab document
            that identifies each batch and lists its test results — once
            it has been reviewed. When a document is not published yet,
            you can request the batch record from our team.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="mps-container py-14 md:py-16 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} data-testid={p.tid} className="mps-card p-7 lg:p-8">
                <div className="h-11 w-11 rounded-[12px] bg-brand-surface border border-brand-border flex items-center justify-center mb-5">
                  <Icon size={20} weight="regular" className="text-brand-navy" aria-hidden />
                </div>
                <div className="font-display text-lg md:text-xl font-semibold text-brand-navy tracking-tight leading-snug mb-2">
                  {p.title}
                </div>
                <p className="text-sm text-brand-muted leading-relaxed">
                  {p.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Documents-by-request callout */}
      <section
        className="mps-container pb-16 md:pb-20 max-w-4xl"
        data-testid="quality-docs-callout"
      >
        <div className="mps-card p-8 lg:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <div className="eyebrow mb-3">Documents by request</div>
            <div className="font-display text-2xl md:text-3xl text-brand-navy font-semibold tracking-tight leading-tight">
              Need the COA for a specific batch?
            </div>
            <p className="mt-3 text-brand-muted leading-relaxed">
              Email us the batch number from your packing list. If the
              document is not yet published in the COA Library, we’ll
              share what’s available for that batch.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <a
              href="mailto:info@medicalpeptidestore.com"
              data-testid="quality-support-email"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-[12px] bg-brand-navy hover:bg-brand-navy2 text-white text-sm font-semibold transition-colors"
            >
              Email support
            </a>
            <Link
              to="/contact"
              data-testid="quality-support-cta"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-[12px] border border-brand-border text-brand-navy hover:bg-brand-surface text-sm font-semibold transition-colors"
            >
              Contact page <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
